// @flow
import {List} from 'immutable';
import {DateTime} from 'luxon';
import * as types from './types';


export function transformResponseToGoals(response: any) {
  return response.data.resources.map((goal) => {
    switch (goal.type) {
      case 'SAVE_FOR_EMERGENCY_FUND':
        return transformResponseToSaveForEmergencyFund(goal);
      case 'SAVE_FOR_CAR':
        return transformResponseToSaveForCar(goal);
      case 'SAVE_FOR_WEDDING':
        return transformResponseToSaveForWedding(goal);
      case 'SAVE_FOR_HOME':
        return transformResponseToSaveForHome(goal);
      case 'SAVE_FOR_VACATION':
        return transformResponseToSaveForVacation(goal);
      case 'SAVE_FOR_OTHER':
      default:
        return transformResponseToSaveForOther(goal);
    }
  });
}

export function transformGoalToRequestData(goal) {
  switch (goal.type) {
    case 'SAVE_FOR_EMERGENCY_FUND':
      return transformEmergencyToRequest(goal);
    case 'SAVE_FOR_CAR':
      return transformCarToRequest(goal);
    case 'SAVE_FOR_WEDDING':
      return transformWeddingToRequest(goal);
    case 'SAVE_FOR_HOME':
      return transformHomeToRequest(goal);
    case 'SAVE_FOR_VACATION':
      return transformVacationToRequest(goal);
    case 'SAVE_FOR_OTHER':
    default:
      return transformOtherToRequest(goal);
  }
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Goal Response Data to Goals
// /////////////////////////////////////////////////////////////////////////////

function transformResponseToSaveForEmergencyFund(data) {
  return types.mkSaveForEmergencyFund({
    ...transformResponseToGoalMap(data),
  });
}

function transformResponseToSaveForWedding(data) {
  return types.mkSaveForWedding({
    ...transformResponseToGoalMap(data),
  });
}

function transformResponseToSaveForCar(data) {
  return types.mkSaveForCar({
    ...transformResponseToGoalMap(data),
  });
}

function transformResponseToSaveForVacation(data) {
  return types.mkSaveForVacation({
    ...transformResponseToGoalMap(data),
  });
}

function transformResponseToSaveForHome(data) {
  return types.mkSaveForHome({
    ...transformResponseToGoalMap(data),
  });
}

function transformResponseToSaveForOther(data) {
  return types.mkSaveForOther({
    ...transformResponseToGoalMap(data),
  });
}
// This function will format DateTime object to ISO given the day passed as param
const convertToISOVariableDay = (date, day) => {
  if (!DateTime.isDateTime(date) && typeof date === "string") return date; // i.e. if already ISO
  const { year, month } = date;
  const dayToUse = day < 10 ? `0${String(day)}` : String(day);
  const monthToUse = month < 10 ? `0${String(month)}` : String(month); // pad with zero
  return `${year}-${monthToUse}-${dayToUse}`;
};
// This function is a workaround/helper to render goal "un-complete" w/ date === epoch
const renderUnComplete = (date) => date !== '1970-01-01' ? date: null;
function transformResponseToGoalMap(data) {
  return {
    id: data.id,
    clientId: data.clientId,
    accountId: data.accountId,
    name: data.name ? data.name : 'Custom',
    targetAmount: data.targetAmount,
    startOn: data.startOn,
    targetOn: data.targetOn,
    contributionAmount: data.contributionAmount,
    tagId: data.tagId,
    imageId: data.imageId,
    contributionFrequency: data.contributionFrequency,
    description: data.description,
    notes: data.notes,
    completedOn: renderUnComplete(data.completedOn),
    isDeleted: data.isDeleted,
    modifiedAt: data.modifiedAt,
    isTakenFromF2S: data.isTakenFromF2S,
  };
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Goal to Goal Request Data
// /////////////////////////////////////////////////////////////////////////////

function transformEmergencyToRequest(goal: types.SaveForEmergencyFund) {
  return {
    type: 'SAVE_FOR_EMERGENCY_FUND',
    ...transformBaseGoalToRequestData(goal),
  };
}

function transformWeddingToRequest(goal: types.SaveForWedding) {
  return {
    type: 'SAVE_FOR_WEDDING',
    ...transformBaseGoalToRequestData(goal),
  };
}

function transformOtherToRequest(goal: types.SaveForCustom) {
  return {
    type: 'SAVE_FOR_OTHER',
    ...transformBaseGoalToRequestData(goal),
  };
}

function transformCarToRequest(goal: types.SaveForCar) {
  return {
    type: 'SAVE_FOR_CAR',
    ...transformBaseGoalToRequestData(goal),
  };
}

function transformHomeToRequest(goal: types.SaveForHome) {
  return {
    type: 'SAVE_FOR_HOME',
    ...transformBaseGoalToRequestData(goal),
  };
}

function transformVacationToRequest(goal: types.SaveForVacation) {
  return {
    type: 'SAVE_FOR_VACATION',
    ...transformBaseGoalToRequestData(goal),
  }
}

function transformBaseGoalToRequestData(goal: types.GoalRecordTypes) {
  return ({
    id: goal.id !== '0' ? goal.id : null,
    clientId: goal.clientId,
    // accountId: goal.accountId,
    name: goal.name,
    targetAmount: goal.targetAmount,
    startOn: goal.startOn && convertToISOVariableDay(goal.startOn, 1),
    targetOn: goal.targetOn && convertToISOVariableDay(goal.targetOn, 28),
    contributionAmount: goal.contributionAmount,
    contributionFrequency: goal.contributionFrequency,
    description: goal.description,
    notes: goal.notes,
    completedOn: goal.completedOn && DateTime.fromISO(goal.completedOn).toFormat('yyyy-LL-dd'),
    isTakenFromF2S: goal.isTakenFromF2S,
  });
}

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Goals to Goal Request Data (batch)
// /////////////////////////////////////////////////////////////////////////////

export function transformGoalsToRequestData(goals: List<types.Goal>) {
  return {
    resources: goals.reduce((list, goal) => { // eslint-disable-line
      list.push(transformGoalToRequestData(goal));
      return list;
    }, []),
  };
}






